import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Phone, Mail, Globe, ExternalLink, ChevronDown, ChevronRight,
  Printer, Send, Loader2, Headphones, User, Shield,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SupportPage({ propertyId, token: urlToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/support/page/${propertyId}/${urlToken}`)
      .then(res => setData(res.data))
      .catch(() => setError('Support page not found or link expired'))
      .finally(() => setLoading(false));
  }, [propertyId, urlToken]);

  const handleSendInfo = async (platform) => {
    if (!sendEmail) return;
    setSending(true);
    const body = `Support Info for ${platform.product_name || platform.platform_name}\n\nPlatform: ${platform.platform_name}\nProduct: ${platform.product_name}\nVendor: ${platform.vendor_name}\nSupport: ${platform.support_contact_name} ${platform.support_contact_email} ${platform.support_contact_phone}\nSupport URL: ${platform.support_url || 'N/A'}\nDocs: ${platform.documentation_url || 'N/A'}`;
    // Use mailto as fallback
    window.location.href = `mailto:${sendEmail}?subject=Support Info: ${platform.product_name || platform.platform_name}&body=${encodeURIComponent(body)}`;
    setSending(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Headphones className="h-12 w-12 mx-auto mb-4 text-slate-300" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">Support Page Not Found</h1>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="support-page">
      {/* Header */}
      <header className="bg-white border-b px-4 py-4 print:border-b-2 print:border-slate-200">
        <div className="max-w-2xl mx-auto">
          <p className="text-[10px] uppercase tracking-widest text-blue-600 font-semibold mb-1">Tech Support Directory</p>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {data.property.name}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">{data.total} technology platforms</p>
        </div>
      </header>

      {/* Platforms */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="space-y-3">
          {Object.entries(data.by_category).map(([catName, platforms]) => (
            <div key={catName}>
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">{catName}</h2>
              <div className="space-y-2">
                {platforms.map((p, i) => {
                  const isExpanded = expandedPlatform === `${p.platform_id}-${i}`;
                  const hasContact = p.support_contact_name || p.support_contact_email || p.support_contact_phone || p.support_url || p.vendor_contacts?.length > 0;
                  return (
                    <div key={`${p.platform_id}-${i}`} className="bg-white rounded-xl border shadow-sm overflow-hidden print:break-inside-avoid">
                      <button
                        onClick={() => setExpandedPlatform(isExpanded ? null : `${p.platform_id}-${i}`)}
                        className="w-full text-left p-3.5 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
                        data-testid={`support-platform-${p.platform_id}`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.category_color }} />
                            <span className="text-sm font-semibold text-slate-900 truncate">{p.platform_name}</span>
                          </div>
                          <p className="text-xs text-slate-500 ml-4 mt-0.5">{p.product_name || p.vendor_name}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {hasContact && <Badge variant="outline" className="text-[9px] text-blue-600 border-blue-200">Support Info</Badge>}
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="border-t bg-slate-50/50 p-4 space-y-3 print:bg-white">
                          {/* Support Contacts */}
                          {(p.support_contact_name || p.support_contact_email || p.support_contact_phone) && (
                            <div className="space-y-1.5">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Headphones className="h-3 w-3" /> Internal Support Contact</p>
                              {p.support_contact_name && <p className="text-sm flex items-center gap-2"><User className="h-3.5 w-3.5 text-slate-400" /> {p.support_contact_name}</p>}
                              {p.support_contact_email && <a href={`mailto:${p.support_contact_email}`} className="text-sm text-blue-600 flex items-center gap-2 hover:underline"><Mail className="h-3.5 w-3.5" /> {p.support_contact_email}</a>}
                              {p.support_contact_phone && <a href={`tel:${p.support_contact_phone}`} className="text-sm text-blue-600 flex items-center gap-2 hover:underline"><Phone className="h-3.5 w-3.5" /> {p.support_contact_phone}</a>}
                            </div>
                          )}

                          {/* Governance */}
                          {(p.platform_admin || p.tech_support) && (
                            <div className="space-y-1">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Shield className="h-3 w-3" /> Responsibility</p>
                              {p.platform_admin && <p className="text-xs text-slate-600">Admin: <span className="font-medium">{p.platform_admin}</span></p>}
                              {p.tech_support && <p className="text-xs text-slate-600">Tech Support: <span className="font-medium">{p.tech_support}</span></p>}
                            </div>
                          )}

                          {/* Vendor Contacts */}
                          {p.vendor_contacts?.length > 0 && (
                            <div className="space-y-1.5">
                              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Vendor Contacts</p>
                              {p.vendor_contacts.map((c, ci) => (
                                <div key={ci} className="flex items-center gap-3 text-sm">
                                  <span className="text-slate-700">{c.name}</span>
                                  {c.role && <Badge variant="outline" className="text-[9px]">{c.role}</Badge>}
                                  {c.email && <a href={`mailto:${c.email}`} className="text-blue-600 text-xs hover:underline">{c.email}</a>}
                                  {c.phone && <a href={`tel:${c.phone}`} className="text-blue-600 text-xs">{c.phone}</a>}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Links */}
                          <div className="flex flex-wrap gap-2">
                            {p.support_url && <a href={p.support_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"><ExternalLink className="h-3 w-3" /> Support Portal</a>}
                            {p.documentation_url && <a href={p.documentation_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-slate-100"><Globe className="h-3 w-3" /> Documentation</a>}
                            {p.vendor_website && <a href={p.vendor_website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-slate-100"><Globe className="h-3 w-3" /> Vendor Site</a>}
                          </div>

                          {/* Send to self */}
                          <div className="flex items-center gap-2 pt-2 border-t print:hidden">
                            <Input placeholder="Your email..." value={sendEmail} onChange={e => setSendEmail(e.target.value)} className="h-8 text-xs flex-1" />
                            <Button size="sm" variant="outline" onClick={() => handleSendInfo(p)} disabled={!sendEmail || sending} className="gap-1 text-xs h-8">
                              <Send className="h-3 w-3" /> Email Info
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Print button */}
        <div className="mt-6 text-center print:hidden">
          <Button variant="outline" onClick={() => window.print()} className="gap-2">
            <Printer className="h-4 w-4" /> Print This Page
          </Button>
        </div>

        {/* Footer */}
        <footer className="mt-8 pt-4 border-t text-center">
          <a href="/help" className="text-xs text-blue-600 hover:underline mb-2 block">Browse all help articles</a>
          <div className="flex items-center justify-center gap-1.5"><img src="/assets/logo.png" alt="" className="h-3.5 w-3.5 opacity-40" /><p className="text-[10px] text-slate-400">Powered by LodgiFlow</p></div>
        </footer>
      </main>
    </div>
  );
}
